import React from 'react';
import styled from 'styled-components';
import Router, { useRouter } from 'next/router';
import { LinkProps } from 'next/link';
import { Box, Flex, IconProps, getOpenNetworkTheme } from '@lifechurch/react-ion';
import { useBeforeUnloadContext } from '@lib/beforeUnloadContext';

type BaseNavLinkProps = React.PropsWithChildren<LinkProps>;

interface NavLinkProps extends BaseNavLinkProps {
  icon: React.FC<IconProps>;
  title: string;
}

interface StyledLinkProps {
  active: boolean;
}

const StyledLink = styled(Box)<StyledLinkProps>`
  color: ${props => (props.active ? props.theme.colors.gray100 : props.theme.colors.gray50)};
  &:hover,
  &:focus {
    color: ${props => props.theme.colors.gray100};
    text-decoration: none;
  }
`;

const NavItem = styled(Flex)<StyledLinkProps>`
  cursor: pointer;
  border-radius: 4px;
  &&:hover,
  &&:focus {
    background-color: ${props => props.theme.colors.action.light};
    * {
      color: ${props => props.theme.colors.action.hover};
      fill: ${props => props.theme.colors.action.hover};
    }
  }
`;

const NavLink: React.FC<NavLinkProps> = ({ icon: Icon, title, ...props }) => {
  const router = useRouter();
  const active =
    router && props.href.toString().split('/')[1].includes(router.asPath.split('/')[1]);
  const activeColor = getOpenNetworkTheme().colors.brands.chop;
  const inactiveColor = getOpenNetworkTheme().colors.gray50;
  const { maybeRouteTo } = useBeforeUnloadContext();

  return (
    <NavItem
      active={active}
      alignItems='center'
      data-testid={`navItem-${title}`}
      mx={3}
      onClick={() => maybeRouteTo(props.href.toString())}
      onMouseOver={() => Router.prefetch(props.href.toString())}
      px={3}
      py='14px'
    >
      <Icon color={active ? activeColor : inactiveColor} />
      <StyledLink
        active={active}
        data-testid={`navLink-${title}`}
        fontSize={2}
        fontWeight={2}
        lineHeight={2}
        marginInlineStart={3}
        textTransform='uppercase'
      >
        {title}
      </StyledLink>
      {props.children}
    </NavItem>
  );
};

export default NavLink;
